import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { ReactNode } from 'react';

import type { OptionType } from '@/api/common';
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxStatus,
  DebouncedCombobox,
  type DebouncedComboboxProps,
  InfiniteComboboxList,
} from '@/components/Form/pickers';
import type { UseInfinitePickerParams } from '@/components/Form/pickers/hooks';

export type InfiniteComboboxPickerSingleProps<T extends OptionType> = Omit<
  DebouncedComboboxProps<T, false>,
  | 'items'
  | 'value'
  | 'onValueChange'
  | 'itemToStringValue'
  | 'itemToStringLabel'
  | 'isItemEqualToValue'
> &
  UseInfinitePickerParams & {
    filteredItems?: T[];
    value?: T | null;
    className?: string;
    disabled?: boolean;
    isLoading?: boolean;
    error?: Error | null;
    placeholder?: string;
    loadingPlaceholder?: string;
    notFoundContent?: ReactNode;
    renderOption?: (option: T) => ReactNode;
  };

const InfiniteComboboxPickerSingle = <T extends OptionType>({
  filteredItems,
  value,
  className,
  disabled = false,
  isLoading = false,
  isFetchingNextPage = false,
  error,
  placeholder = t`Select...`,
  loadingPlaceholder = t`Loading...`,
  notFoundContent,
  renderOption,
  fetchNextPage,
  loadMore,
  hasMore,
  loadingOffsetPx,
  ...rest
}: InfiniteComboboxPickerSingleProps<T>) => {
  return (
    <DebouncedCombobox
      {...rest}
      filteredItems={filteredItems}
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      isItemEqualToValue={(item, selected) => item.id === selected.id}
      isLoading={isLoading || isFetchingNextPage}
      error={error}
      disabled={disabled}
      value={value ?? null}
    >
      <ComboboxInput
        className={className}
        placeholder={isLoading ? loadingPlaceholder : placeholder}
      />
      <ComboboxContent>
        <ComboboxStatus />
        <ComboboxEmpty>{notFoundContent ?? <Trans>No items were found...</Trans>}</ComboboxEmpty>
        <InfiniteComboboxList
          fetchNextPage={fetchNextPage}
          loadMore={loadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          loadingOffsetPx={loadingOffsetPx}
        >
          {(item: T) => (
            <ComboboxItem key={item.id} value={item} disabled={item.disabled}>
              {renderOption ? renderOption(item) : item.name}
            </ComboboxItem>
          )}
        </InfiniteComboboxList>
      </ComboboxContent>
    </DebouncedCombobox>
  );
};

export default InfiniteComboboxPickerSingle;
